<template>
  <el-dialog
    title="GLB光感调节"
    :visible.sync="dialogVisible"
    width="1120px"
    append-to-body
    class="glb-appearance-dialog"
    @closed="handleClosed"
  >
    <div class="appearance_wrap" v-loading="loading">
      <div class="preview_panel">
        <div ref="preview" class="preview_canvas"></div>
        <div class="preview_tip">
          拖拽可自由查看模型，亮度和补光会实时作用到预览模型，便于你在上传时就调好默认观感。
        </div>
      </div>
      <div class="config_panel">
        <div class="material_name">{{ resourceName || "GLB花材" }}</div>
        <div class="desc">
          这里会把素材默认光感直接烘焙进 GLB 模型文件里，后续在其他编辑器打开时，也能明显看到你在后台调好的亮度和补光观感。
        </div>
        <div class="view_group">
          <div class="group_title">视角</div>
          <el-button size="mini" @click="setPreviewView('front')">正视</el-button>
          <el-button size="mini" @click="setPreviewView('side')">侧视</el-button>
          <el-button size="mini" @click="setPreviewView('top')">俯视</el-button>
        </div>

        <div class="value_group">
          <div class="group_title">亮度</div>
          <div class="slider_row">
            <span>亮度</span>
            <el-slider
              v-model="appearanceConfig.brightness"
              :min="40"
              :max="180"
              :step="1"
              :show-tooltip="false"
              @input="applyAppearanceToPreview"
            />
            <b>{{ Math.round(appearanceConfig.brightness) }}%</b>
          </div>
        </div>

        <div class="collapsible_group">
          <button
            type="button"
            class="group_toggle"
            @click="materialPanelExpanded = !materialPanelExpanded"
          >
            <div>
              <div class="group_title">材质参数</div>
              <div class="group_toggle__summary">
                这里只保留补光和补光色，方便把默认发光感稳定写进模型
              </div>
            </div>
            <i
              :class="
                materialPanelExpanded
                  ? 'el-icon-arrow-up'
                  : 'el-icon-arrow-down'
              "
            ></i>
          </button>
          <div v-show="materialPanelExpanded" class="group_body">
            <div class="model-color-grid">
              <div class="slider_row compact">
                <span>补光</span>
                <el-slider
                  v-model="appearanceConfig.emissiveStrength"
                  :min="0"
                  :max="40"
                  :step="1"
                  :show-tooltip="false"
                  @input="applyAppearanceToPreview"
                />
                <b>{{ Math.round(appearanceConfig.emissiveStrength) }}%</b>
              </div>
              <div class="slider_row compact">
                <span>高光</span>
                <el-slider
                  v-model="appearanceConfig.specularIntensity"
                  :min="0"
                  :max="100"
                  :step="1"
                  :show-tooltip="false"
                  @input="applyAppearanceToPreview"
                />
                <b>{{ Math.round(appearanceConfig.specularIntensity) }}%</b>
              </div>
              <label class="color_row">
                <span>补光色</span>
                <input
                  v-model="appearanceConfig.emissiveColor"
                  type="color"
                  @input="applyAppearanceToPreview"
                />
              </label>
            </div>
          </div>
        </div>

        <div class="summary_group">
          <div class="group_title">当前摘要</div>
          <div class="summary_tags">
            <el-tag size="mini">亮度 {{ Math.round(appearanceConfig.brightness) }}%</el-tag>
            <el-tag size="mini">补光 {{ Math.round(appearanceConfig.emissiveStrength) }}%</el-tag>
            <el-tag size="mini">补光色 {{ appearanceConfig.emissiveColor }}</el-tag>
          </div>
        </div>

        <div class="actions">
          <el-button size="small" @click="applyPlantPreset">植物预设</el-button>
          <el-button size="small" @click="resetToDefault">重置默认</el-button>
          <el-button size="small" @click="cancel">取消</el-button>
          <el-button type="primary" size="small" @click="confirmSave">
            确认保存光感
          </el-button>
        </div>
      </div>
    </div>
  </el-dialog>
</template>

<script>
import * as THREE from "three";
import { OrbitControls } from "three/examples/jsm/controls/OrbitControls.js";
import { GLTFLoader } from "three/examples/jsm/loaders/GLTFLoader.js";
import { MeshoptDecoder } from "three/examples/jsm/libs/meshopt_decoder.module.js";
import {
  createDefaultGlbColorAdjust,
  createPlantGlbColorPreset,
  normalizeGlbColorAdjust,
} from "../utils/glbAppearance";

export default {
  data() {
    return {
      dialogVisible: false,
      loading: false,
      resourceKey: "",
      resourceName: "",
      modelUrl: "",
      sourceFileName: "",
      appearanceConfig: createDefaultGlbColorAdjust(),
      materialPanelExpanded: false,
      scene: null,
      camera: null,
      renderer: null,
      controls: null,
      modelRoot: null,
      modelBounds: null,
      previewLightGroup: null,
      previewShadowPlane: null,
      rafId: 0,
    };
  },
  methods: {
    async open(payload = {}) {
      this.dialogVisible = true;
      this.loading = true;
      this.resourceKey = String(
        payload.resourceKey || payload.id || payload.modelUrl || ""
      );
      this.resourceName = payload.resourceName || payload.name || "";
      this.modelUrl = payload.modelUrl || "";
      this.sourceFileName = payload.sourceFileName || "";
      this.appearanceConfig = normalizeGlbColorAdjust(
        payload.modelColorAdjust || payload.colorAdjust
      );
      this.materialPanelExpanded = false;
      await this.$nextTick();
      this.initPreview();
    },
    initPreview() {
      this.disposePreview();
      const mount = this.$refs.preview;
      if (!mount || !this.modelUrl) {
        this.loading = false;
        return;
      }
      this.scene = new THREE.Scene();
      this.scene.background = new THREE.Color(0xffffff);

      const width = mount.clientWidth || 660;
      const height = mount.clientHeight || 540;
      this.camera = new THREE.PerspectiveCamera(35, width / height, 0.01, 100);
      this.camera.position.set(0.8, 0.8, 1.2);

      this.renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true });
      this.renderer.setPixelRatio(window.devicePixelRatio || 1);
      this.renderer.setSize(width, height);
      this.renderer.shadowMap.enabled = false;
      mount.innerHTML = "";
      mount.appendChild(this.renderer.domElement);

      this.controls = new OrbitControls(this.camera, this.renderer.domElement);
      this.controls.enableDamping = true;
      this.controls.dampingFactor = 0.08;

      const loader = new GLTFLoader();
      loader.setMeshoptDecoder(MeshoptDecoder);
      loader.load(
        this.modelUrl,
        (gltf) => {
          this.modelRoot = (gltf.scene || gltf.scenes[0] || new THREE.Group()).clone(
            true
          );
          this.modelRoot.traverse((node) => {
            if (!node.isMesh) {
              return;
            }
            node.castShadow = false;
            node.receiveShadow = false;
            if (Array.isArray(node.material)) {
              node.material = node.material.map((material) => material.clone());
            } else if (node.material) {
              node.material = node.material.clone();
            }
          });
          this.scene.add(this.modelRoot);
          this.modelBounds = new THREE.Box3().setFromObject(this.modelRoot);
          this.setupPreviewEnvironment();
          this.fitCameraToModel();
          this.applyAppearanceToPreview();
          this.renderLoop();
          this.loading = false;
        },
        undefined,
        () => {
          this.$message.error("GLB预览加载失败");
          this.loading = false;
          this.dialogVisible = false;
        }
      );
    },
    clearPreviewEnvironment() {
      if (this.previewLightGroup && this.scene) {
        this.scene.remove(this.previewLightGroup);
      }
      this.previewLightGroup = null;

      if (this.previewShadowPlane) {
        if (this.scene) {
          this.scene.remove(this.previewShadowPlane);
        }
        if (this.previewShadowPlane.geometry) {
          this.previewShadowPlane.geometry.dispose();
        }
        if (this.previewShadowPlane.material) {
          this.previewShadowPlane.material.dispose();
        }
      }
      this.previewShadowPlane = null;
    },
    setupPreviewEnvironment() {
      if (!this.scene || !this.modelBounds) {
        return;
      }
      this.clearPreviewEnvironment();
      const center = this.modelBounds.getCenter(new THREE.Vector3());
      const size = this.modelBounds.getSize(new THREE.Vector3());
      const radius = Math.max(size.x, size.y, size.z) || 0.6;

      const lights = new THREE.Group();

      // Keep the preview closer to the real editor canvas instead of the brighter OA mock scene.
      const ambient = new THREE.AmbientLight(0xefefef, 0.7);
      lights.add(ambient);

      const sunLight = new THREE.DirectionalLight(0xffffff, 0.4);
      sunLight.position.set(
        center.x + radius * 2,
        center.y + radius * 2.5,
        center.z + radius * 2
      );
      lights.add(sunLight);

      const sideLight = new THREE.DirectionalLight(0xe9eef8, 0);
      sideLight.position.set(
        center.x - radius * 1.5,
        center.y + radius,
        center.z - radius
      );
      lights.add(sideLight);

      const bottomLight = new THREE.DirectionalLight(0xf3f0e5, 0);
      bottomLight.position.set(center.x, center.y - radius * 0.6, center.z);
      lights.add(bottomLight);

      const pointLight = new THREE.PointLight(0xffedd8, 0, radius * 8);
      pointLight.position.set(center.x, center.y + radius * 1.4, center.z);
      pointLight.decay = 2;
      lights.add(pointLight);

      this.previewLightGroup = lights;
      this.previewShadowPlane = null;
      this.scene.add(lights);
    },
    fitCameraToModel() {
      if (!this.modelBounds || !this.camera || !this.controls) {
        return;
      }
      const center = this.modelBounds.getCenter(new THREE.Vector3());
      const size = this.modelBounds.getSize(new THREE.Vector3());
      const radius = Math.max(size.x, size.y, size.z) * 0.8 || 0.6;
      this.controls.target.copy(center);
      this.camera.position.set(
        center.x + radius * 1.2,
        center.y + radius * 1.1,
        center.z + radius * 1.6
      );
      this.camera.near = 0.01;
      this.camera.far = 20;
      this.camera.updateProjectionMatrix();
      this.controls.update();
    },
    setPreviewView(type) {
      if (!this.modelBounds || !this.camera || !this.controls) {
        return;
      }
      const center = this.modelBounds.getCenter(new THREE.Vector3());
      const size = this.modelBounds.getSize(new THREE.Vector3());
      const radius = Math.max(size.x, size.y, size.z) * 1.8 || 1;
      const positions = {
        front: new THREE.Vector3(center.x, center.y, center.z + radius),
        side: new THREE.Vector3(center.x + radius, center.y, center.z),
        top: new THREE.Vector3(center.x, center.y + radius, center.z + 0.001),
      };
      this.controls.target.copy(center);
      this.camera.position.copy(positions[type] || positions.front);
      this.camera.lookAt(center);
      this.camera.updateProjectionMatrix();
      this.controls.update();
    },
    applyAppearanceToPreview() {
      if (!this.modelRoot) {
        return;
      }
      const nextAdjust = normalizeGlbColorAdjust(this.appearanceConfig);
      this.appearanceConfig = { ...nextAdjust };
      this.modelRoot.traverse((child) => {
        if (!child.isMesh || !child.material) {
          return;
        }
        const materials = Array.isArray(child.material)
          ? child.material
          : [child.material];
        materials.forEach((material) => {
          let adjustedPreviewColor = null;
          if (!material.userData.baseColor && material.color) {
            material.userData.baseColor = material.color.clone();
          }
          if (
            material.roughness !== undefined &&
            material.userData.baseRoughness === undefined
          ) {
            material.userData.baseRoughness = material.roughness;
          }
          if (
            material.metalness !== undefined &&
            material.userData.baseMetalness === undefined
          ) {
            material.userData.baseMetalness = material.metalness;
          }
          if (material.normalScale && !material.userData.baseNormalScale) {
            material.userData.baseNormalScale = material.normalScale.clone();
          }
          if (material.emissive && !material.userData.baseEmissive) {
            material.userData.baseEmissive = material.emissive.clone();
          }
          if (
            material.emissiveIntensity !== undefined &&
            material.userData.baseEmissiveIntensity === undefined
          ) {
            material.userData.baseEmissiveIntensity = material.emissiveIntensity;
          }
          if (
            material.specularIntensity !== undefined &&
            material.userData.baseSpecularIntensity === undefined
          ) {
            material.userData.baseSpecularIntensity = material.specularIntensity;
          }
          if (material.specularColor && !material.userData.baseSpecularColor) {
            material.userData.baseSpecularColor = material.specularColor.clone();
          }
          if (
            material.opacity !== undefined &&
            material.userData.baseOpacity === undefined
          ) {
            material.userData.baseOpacity = material.opacity;
          }
          if (material.color && material.userData.baseColor) {
            const baseColor = material.userData.baseColor.clone();
            baseColor.r = THREE.MathUtils.clamp(
              baseColor.r * (nextAdjust.redBoost / 100),
              0,
              1
            );
            baseColor.g = THREE.MathUtils.clamp(
              baseColor.g * (nextAdjust.greenBoost / 100),
              0,
              1
            );
            baseColor.b = THREE.MathUtils.clamp(
              baseColor.b * (nextAdjust.blueBoost / 100),
              0,
              1
            );
            const hsl = {};
            baseColor.getHSL(hsl);
            const hue = ((nextAdjust.hue / 360 + hsl.h) % 1 + 1) % 1;
            const saturation = Math.min(
              1,
              Math.max(0, hsl.s * (nextAdjust.saturation / 100))
            );
            const lightness = Math.min(
              1,
              Math.max(0, hsl.l * (nextAdjust.brightness / 100))
            );
            material.color.setHSL(hue, saturation, lightness);
            adjustedPreviewColor = material.color.clone();
          }
          if (
            material.roughness !== undefined &&
            material.userData.baseRoughness !== undefined
          ) {
            material.roughness = THREE.MathUtils.clamp(
              material.userData.baseRoughness * (nextAdjust.roughnessMul / 100) +
                nextAdjust.roughnessBias / 100 +
                this.getSpecularRoughnessFallback(nextAdjust),
              0,
              1
            );
          }
          if (
            material.metalness !== undefined &&
            material.userData.baseMetalness !== undefined
          ) {
            material.metalness = THREE.MathUtils.clamp(
              material.userData.baseMetalness * (nextAdjust.metalnessMul / 100),
              0,
              1
            );
          }
          if (material.normalScale && material.userData.baseNormalScale) {
            material.normalScale
              .copy(material.userData.baseNormalScale)
              .multiplyScalar(nextAdjust.normalScale / 100);
          }
          if (material.emissive) {
            const nextEmissive = this.getCombinedEmissiveFactor(
              adjustedPreviewColor || material.color,
              nextAdjust
            );
            material.emissive.setRGB(
              nextEmissive[0],
              nextEmissive[1],
              nextEmissive[2]
            );
          }
          if (material.emissiveIntensity !== undefined) {
            material.emissiveIntensity = 1;
          }
          if (material.specularIntensity !== undefined) {
            const baseSpecularIntensity =
              material.userData.baseSpecularIntensity === undefined
                ? 1
                : material.userData.baseSpecularIntensity;
            material.specularIntensity = THREE.MathUtils.clamp(
              baseSpecularIntensity * (nextAdjust.specularIntensity / 100),
              0,
              1
            );
          }
          if (material.specularColor) {
            material.specularColor.set(nextAdjust.specularColor);
          }
          if (material.opacity !== undefined) {
            const baseOpacity =
              material.userData.baseOpacity === undefined
                ? 1
                : material.userData.baseOpacity;
            material.opacity = THREE.MathUtils.clamp(
              baseOpacity * (nextAdjust.opacity / 100),
              0,
              1
            );
            material.transparent = material.opacity < 0.999;
          }
          material.needsUpdate = true;
        });
      });
    },
    applyPlantPreset() {
      this.appearanceConfig = createPlantGlbColorPreset();
      this.applyAppearanceToPreview();
    },
    buildAppearanceFileName() {
      const rawName = this.sourceFileName || this.resourceName || "glb-model";
      if (/\.glb$/i.test(rawName)) {
        return rawName.replace(/\.glb$/i, "-appearance.glb");
      }
      return `${rawName}-appearance.glb`;
    },
    getAdjustedBaseColor(baseColorFactor, nextAdjust) {
      const sourceFactor = Array.isArray(baseColorFactor)
        ? baseColorFactor
        : [1, 1, 1, 1];
      const color = new THREE.Color(
        sourceFactor[0] ?? 1,
        sourceFactor[1] ?? 1,
        sourceFactor[2] ?? 1
      );
      color.r = THREE.MathUtils.clamp(
        color.r * (nextAdjust.redBoost / 100),
        0,
        1
      );
      color.g = THREE.MathUtils.clamp(
        color.g * (nextAdjust.greenBoost / 100),
        0,
        1
      );
      color.b = THREE.MathUtils.clamp(
        color.b * (nextAdjust.blueBoost / 100),
        0,
        1
      );
      const hsl = {};
      color.getHSL(hsl);
      const hue = ((nextAdjust.hue / 360 + hsl.h) % 1 + 1) % 1;
      const saturation = THREE.MathUtils.clamp(
        hsl.s * (nextAdjust.saturation / 100),
        0,
        1
      );
      const lightness = THREE.MathUtils.clamp(
        hsl.l * (nextAdjust.brightness / 100),
        0,
        1
      );
      color.setHSL(hue, saturation, lightness);
      return color;
    },
    getBrightnessLift(nextAdjust) {
      return THREE.MathUtils.clamp(
        (Number(nextAdjust.brightness || 100) - 100) / 90,
        0,
        0.9
      );
    },
    getSpecularRoughnessFallback(nextAdjust) {
      return THREE.MathUtils.clamp(
        ((100 - Number(nextAdjust.specularIntensity || 100)) / 100) * 0.35,
        0,
        0.35
      );
    },
    getCombinedEmissiveFactor(baseColorInput, nextAdjust) {
      const baseColor =
        baseColorInput && baseColorInput.isColor
          ? baseColorInput
          : new THREE.Color(baseColorInput || "#ffffff");
      const emissiveColor = new THREE.Color(
        nextAdjust.emissiveColor || "#4c7d2a"
      );
      const emissiveStrength = Math.max(
        0,
        Number(nextAdjust.emissiveStrength || 0) / 100
      );
      const brightnessLift = this.getBrightnessLift(nextAdjust);
      return [
        THREE.MathUtils.clamp(
          baseColor.r * brightnessLift + emissiveColor.r * emissiveStrength,
          0,
          1
        ),
        THREE.MathUtils.clamp(
          baseColor.g * brightnessLift + emissiveColor.g * emissiveStrength,
          0,
          1
        ),
        THREE.MathUtils.clamp(
          baseColor.b * brightnessLift + emissiveColor.b * emissiveStrength,
          0,
          1
        ),
      ];
    },
    applyAppearanceToDocument(document, nextAdjust, SpecularExtensionClass) {
      const root = document.getRoot();
      let fallbackMaterial = null;
      root.listMeshes().forEach((mesh) => {
        mesh.listPrimitives().forEach((primitive) => {
          if (!primitive.getMaterial()) {
            if (!fallbackMaterial) {
              fallbackMaterial = document.createMaterial("GLB Appearance Material");
            }
            primitive.setMaterial(fallbackMaterial);
          }
        });
      });
      const materials = root.listMaterials();
      if (!materials.length) {
        throw new Error("当前模型没有可调整的材质参数");
      }
      const needsSpecularExtension =
        nextAdjust.specularIntensity !== 100 ||
        String(nextAdjust.specularColor || "").toLowerCase() !== "#ffffff";
      const specularExtension =
        needsSpecularExtension || materials.some((material) => {
          return !!material.getExtension("KHR_materials_specular");
        })
          ? document.createExtension(SpecularExtensionClass)
          : null;
      const emissiveColor = new THREE.Color(
        nextAdjust.emissiveColor || "#4c7d2a"
      );
      const specularColor = new THREE.Color(
        nextAdjust.specularColor || "#ffffff"
      );
      materials.forEach((material) => {
        const baseColorFactor = material.getBaseColorFactor();
        const adjustedColor = this.getAdjustedBaseColor(baseColorFactor, nextAdjust);
        const combinedEmissive = this.getCombinedEmissiveFactor(
          adjustedColor,
          nextAdjust
        );
        const nextOpacity = THREE.MathUtils.clamp(
          (baseColorFactor[3] ?? 1) * (nextAdjust.opacity / 100),
          0,
          1
        );
        material.setBaseColorFactor([
          adjustedColor.r,
          adjustedColor.g,
          adjustedColor.b,
          nextOpacity,
        ]);
        if (nextOpacity < 0.999 && material.getAlphaMode() === "OPAQUE") {
          material.setAlphaMode("BLEND");
        }
        material.setRoughnessFactor(
          THREE.MathUtils.clamp(
            material.getRoughnessFactor() * (nextAdjust.roughnessMul / 100) +
              nextAdjust.roughnessBias / 100 +
              this.getSpecularRoughnessFallback(nextAdjust),
            0,
            1
          )
        );
        material.setMetallicFactor(
          THREE.MathUtils.clamp(
            material.getMetallicFactor() * (nextAdjust.metalnessMul / 100),
            0,
            1
          )
        );
        material.setNormalScale(
          THREE.MathUtils.clamp(
            material.getNormalScale() * (nextAdjust.normalScale / 100),
            0,
            10
          )
        );
        material.setEmissiveFactor(combinedEmissive);
        if (!specularExtension) {
          return;
        }
        const existingSpecular = material.getExtension("KHR_materials_specular");
        const specular =
          existingSpecular || specularExtension.createSpecular();
        specular.setSpecularFactor(
          THREE.MathUtils.clamp(nextAdjust.specularIntensity / 100, 0, 1)
        );
        specular.setSpecularColorFactor([
          specularColor.r,
          specularColor.g,
          specularColor.b,
        ]);
        material.setExtension("KHR_materials_specular", specular);
      });
    },
    async exportCurrentModelAsGlb() {
      if (!this.modelUrl) {
        throw new Error("当前没有可处理的 GLB 模型地址");
      }
      const nextAdjust = normalizeGlbColorAdjust(this.appearanceConfig);
      const [
        { WebIO },
        { ALL_EXTENSIONS, KHRMaterialsSpecular },
        { MeshoptEncoder, MeshoptDecoder },
      ] = await Promise.all([
        import("@gltf-transform/core"),
        import("@gltf-transform/extensions"),
        import("meshoptimizer"),
      ]);
      if (MeshoptDecoder && MeshoptDecoder.ready) {
        await MeshoptDecoder.ready;
      }
      if (MeshoptEncoder && MeshoptEncoder.ready) {
        await MeshoptEncoder.ready;
      }
      const response = await fetch(this.modelUrl);
      if (!response.ok) {
        throw new Error("原始 GLB 模型读取失败");
      }
      const inputBytes = new Uint8Array(await response.arrayBuffer());
      const io = new WebIO()
        .registerExtensions(ALL_EXTENSIONS)
        .registerDependencies({
          "meshopt.decoder": MeshoptDecoder,
          "meshopt.encoder": MeshoptEncoder,
        });
      const document = await io.readBinary(inputBytes);
      this.applyAppearanceToDocument(document, nextAdjust, KHRMaterialsSpecular);
      const outputBytes = await io.writeBinary(document);
      return new File([outputBytes], this.buildAppearanceFileName(), {
        type: "model/gltf-binary",
      });
    },
    resetToDefault() {
      this.appearanceConfig = createDefaultGlbColorAdjust();
      this.applyAppearanceToPreview();
    },
    renderLoop() {
      cancelAnimationFrame(this.rafId);
      const render = () => {
        this.rafId = requestAnimationFrame(render);
        if (this.controls) {
          this.controls.update();
        }
        if (this.renderer && this.scene && this.camera) {
          this.renderer.render(this.scene, this.camera);
        }
      };
      render();
    },
    cancel() {
      this.dialogVisible = false;
    },
    async confirmSave() {
      if (!this.modelRoot || !this.modelUrl) {
        return;
      }
      this.loading = true;
      try {
        const colorAdjust = normalizeGlbColorAdjust(this.appearanceConfig);
        const bakedFile = await this.exportCurrentModelAsGlb();
        this.$emit("confirm", {
          colorAdjust,
          colorAdjustJson: JSON.stringify(colorAdjust),
          bakedFile,
        });
        this.dialogVisible = false;
      } catch (error) {
        console.error("GLB appearance bake failed:", error);
        this.$message.error(
          (error && error.message) || "GLB 光感烘焙失败，请重试"
        );
      } finally {
        this.loading = false;
      }
    },
    handleClosed() {
      this.disposePreview();
      this.loading = false;
      this.resourceKey = "";
      this.resourceName = "";
      this.modelUrl = "";
      this.sourceFileName = "";
      this.appearanceConfig = createDefaultGlbColorAdjust();
      this.materialPanelExpanded = false;
    },
    disposePreview() {
      cancelAnimationFrame(this.rafId);
      this.rafId = 0;
      this.clearPreviewEnvironment();
      if (this.controls) {
        this.controls.dispose();
        this.controls = null;
      }
      if (this.modelRoot) {
        this.modelRoot.traverse((node) => {
          if (node.geometry) {
            node.geometry.dispose && node.geometry.dispose();
          }
          const materials = Array.isArray(node.material)
            ? node.material
            : node.material
              ? [node.material]
              : [];
          materials.forEach((material) => {
            if (material.map) {
              material.map.dispose();
            }
            material.dispose && material.dispose();
          });
        });
        if (this.modelRoot.parent) {
          this.modelRoot.parent.remove(this.modelRoot);
        }
        this.modelRoot = null;
      }
      if (this.renderer) {
        const mount = this.$refs.preview;
        if (mount && this.renderer.domElement) {
          mount.contains(this.renderer.domElement) &&
            mount.removeChild(this.renderer.domElement);
        }
        this.renderer.dispose();
        this.renderer.forceContextLoss && this.renderer.forceContextLoss();
        this.renderer = null;
      }
      this.scene = null;
      this.camera = null;
      this.modelBounds = null;
      this.previewLightGroup = null;
      this.previewShadowPlane = null;
    },
  },
};
</script>

<style lang="scss" scoped>
.appearance_wrap {
  display: flex;
  gap: 18px;
}

.preview_panel {
  flex: 1;
  min-width: 0;
}

.preview_canvas {
  width: 100%;
  height: 560px;
  border-radius: 14px;
  overflow: hidden;
  border: 1px solid #ebeef5;
  background: #f8f7f3;
}

.preview_tip {
  margin-top: 10px;
  font-size: 12px;
  line-height: 1.7;
  color: #909399;
}

.config_panel {
  width: 360px;
  flex-shrink: 0;
}

.material_name {
  font-size: 20px;
  font-weight: 600;
  color: #303133;
  margin-bottom: 10px;
}

.desc {
  font-size: 13px;
  line-height: 1.7;
  color: #909399;
  margin-bottom: 14px;
}

.view_group,
.value_group,
.summary_group {
  padding: 14px 16px;
  border-radius: 12px;
  background: #f8faf4;
  margin-bottom: 14px;
}

.group_title {
  font-size: 14px;
  font-weight: 600;
  color: #606266;
  margin-bottom: 10px;
}

.slider_row {
  display: grid;
  grid-template-columns: 56px minmax(0, 1fr) 48px;
  gap: 10px;
  align-items: center;
}

.slider_row + .slider_row {
  margin-top: 10px;
}

.slider_row.compact {
  grid-template-columns: 64px minmax(0, 1fr) 48px;
}

.slider_row span,
.slider_row b {
  font-size: 12px;
  color: #606266;
}

.slider_row b {
  text-align: right;
  color: #8bb229;
}

.collapsible_group {
  border-radius: 12px;
  background: #f8faf4;
  margin-bottom: 14px;
  overflow: hidden;
}

.group_toggle {
  width: 100%;
  border: 0;
  padding: 14px 16px;
  background: transparent;
  display: flex;
  align-items: center;
  justify-content: space-between;
  cursor: pointer;
  text-align: left;
}

.group_toggle__summary {
  font-size: 12px;
  color: #909399;
  line-height: 1.6;
}

.group_body {
  padding: 0 16px 16px;
}

.model-color-grid {
  display: grid;
  gap: 10px;
}

.color_row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  font-size: 12px;
  color: #606266;
}

.color_row input {
  width: 56px;
  height: 32px;
  border: 0;
  padding: 0;
  background: transparent;
}

.summary_tags {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.actions {
  display: flex;
  gap: 10px;
  justify-content: flex-end;
}
</style>
